import { ChangeDetectionStrategy, Component, computed, input } from '@angular/core';
import { DatePipe } from '@angular/common';
import { TranslocoModule } from '@jsverse/transloco';

import type { AssessmentRecord } from '../../../../models';
import {
  ART49_DEROGATIONS,
  type Art49DerogationId,
} from '../../../../models/assessments/tia.model';
import { countryNameByCode } from '../../../../models/iso-3166.model';
import { recordOnlyStateLabelKey } from '../../../../utils/assessment.utils';

/**
 * Read-only summary of a record-only TIA (VER-377) — the transfer is
 * exempt (most often "No TIA needed — record the derogation") and this
 * record documents why, for audit. Shown in place of the questionnaire /
 * report flow by the assessment drawer and the editor.
 */
@Component({
  selector: 'app-tia-record-summary',
  imports: [DatePipe, TranslocoModule],
  templateUrl: './tia-record-summary.html',
  styleUrl: './tia-record-summary.css',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class TiaRecordSummaryComponent {
  readonly assessment = input.required<AssessmentRecord>();
  /** Display name of the linked processing activity, resolved by the host
   *  (the drawer already fetches it). `null` while unresolved / on error. */
  readonly activityName = input<string | null>(null);

  /** Whether the transfer was recorded against a processing activity. */
  protected readonly hasActivity = computed(() => !!this.assessment().pa_link?.activity_id);

  protected readonly outcomeKey = computed(() => recordOnlyStateLabelKey(this.assessment()));

  protected readonly vendorName = computed(
    () => this.assessment().vendor_link?.vendor_name ?? null
  );

  protected readonly countryName = computed(() =>
    countryNameByCode(this.assessment().vendor_link?.country_code)
  );

  /** Claimed derogations in picker order; empty for EEA / adequacy
   *  records and for legacy claims that never carried ids. */
  protected readonly derogationLabelKeys = computed(() => {
    const ids = new Set<Art49DerogationId>(
      this.assessment().vendor_link?.art49_derogation?.derogation_ids ?? []
    );
    return ART49_DEROGATIONS.filter(o => ids.has(o.id)).map(o => o.labelKey);
  });

  /** The user's rationale for an Art. 49 claim, else the gate's own
   *  explanation (EEA membership / adequacy decision). */
  protected readonly rationale = computed(() => {
    const a = this.assessment();
    return (
      a.vendor_link?.art49_derogation?.rationale ||
      (a.risk_profile as { gate_rationale?: string } | null | undefined)?.gate_rationale ||
      null
    );
  });

  protected readonly nextReviewAt = computed(
    () => this.assessment().vendor_link?.next_review_at ?? null
  );
}
